import { env, isConfigured } from '../../config/env';
import { Card, SkeletonBlock, SkeletonLine } from '../../components/Card';
import { IconButton, Pill } from '../../components/ui';
import { formatClockTime, localDateKey } from '../../lib/date';
import { useLocalDateKey } from '../../lib/storage';
import { useWeather } from '../../lib/weather/useWeather';
import { AlertStrip } from './AlertStrip';
import { ForecastStrip } from './ForecastStrip';

/**
 * Weather: current conditions + 5-day forecast + prominent severe alerts.
 *
 * Layout budget (measured card sizes: 465x494 / 465x429 / 369x386):
 *   [alert strip — conditional, full-bleed banner]
 *   [hero      — glyph + big temperature + condition + location]
 *   [stat row  — feels like / today's high / today's low]
 *   [forecast  — 5 columns, pinned to the bottom with mt-auto]
 *
 * Every state is designed, and the zero-config state is a REAL one: with no
 * VITE_OPENWEATHER_API_KEY the data comes from api.open-meteo.com (keyless)
 * and the header carries an "add key" badge. See DECISIONS.md.
 */
export function WeatherPanel() {
  const { status, now, forecast, alerts, error, lastUpdated, refresh, usingKeylessFallback } =
    useWeather();
  const todayKey = useLocalDateKey();
  const metric = env.weather.units === 'metric';
  const unit = metric ? '°C' : '°F';

  const today = forecast.find((d) => d.dateKey === todayKey) ?? forecast[0];

  return (
    <Card
      title="Weather"
      icon="🌤"
      accent="sky"
      testId="weather-panel"
      status={status}
      badge={<KeyBadge usingKeylessFallback={usingKeylessFallback} />}
      banner={alerts.length > 0 ? <AlertStrip alerts={alerts} /> : undefined}
      action={
        <IconButton label="Refresh weather" onClick={refresh}>
          ⟳
        </IconButton>
      }
      loadingLabel={`Reading the sky over ${env.weather.locationName}…`}
      loadingSkeleton={<WeatherSkeleton />}
      errorMessage={error ?? "Couldn't reach the weather service."}
      onRetry={refresh}
      unconfiguredMessage="Weather is unavailable right now."
      hint={
        // Two different dead ends: 'unconfigured' means even the keyless
        // source failed, 'error' means a configured key AND the rescue failed.
        status === 'unconfigured' ? (
          <>
            The keyless source (open-meteo) could not be reached either. Add{' '}
            <code className="rounded bg-panel-raised px-1">VITE_OPENWEATHER_API_KEY</code> to{' '}
            <code className="rounded bg-panel-raised px-1">.env</code> and restart the dev server.
          </>
        ) : (
          `Showing nothing for ${env.weather.locationName}. Check your connection.`
        )
      }
    >
      {now !== null && (
        <div className="flex h-full min-h-0 flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-[38px] leading-none" aria-hidden>
              {now.icon}
            </span>
            <div className="min-w-0">
              <div className="flex items-baseline gap-1">
                <span className="text-[34px] font-semibold leading-none tabular-nums text-ink">
                  {Math.round(metric ? now.tempC : now.tempF)}
                </span>
                <span className="text-sm font-medium text-ink-subtle">{unit}</span>
              </div>
              <p className="truncate text-[13px] font-medium text-ink-muted">
                {now.conditionLabel}
              </p>
              <p className="truncate text-[11px] text-ink-subtle">{now.locationName}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 rounded-lg bg-panel-raised px-2 py-1.5">
            <Stat label="Feels like" value={`${Math.round(metric ? now.feelsLikeC : now.feelsLikeF)}°`} />
            <Stat
              label="High"
              value={today === undefined ? '—' : `${Math.round(metric ? today.highC : today.highF)}°`}
            />
            <Stat
              label="Low"
              value={today === undefined ? '—' : `${Math.round(metric ? today.lowC : today.lowF)}°`}
            />
          </div>

          <div className="mt-auto flex flex-col gap-1">
            <ForecastStrip days={forecast} units={env.weather.units} todayKey={todayKey} />
            <p className="text-right text-[10px] text-ink-subtle">{freshness(lastUpdated)}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 text-center">
      <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </p>
      <p className="text-[13px] font-semibold tabular-nums text-ink">{value}</p>
    </div>
  );
}

/**
 * The spec's "configure your API key" affordance, kept as a badge rather than
 * an empty card because the card is showing real data underneath it.
 * `data-testid="weather-unconfigured"` is the verification suite's hook, and
 * it renders in EVERY status, because the Card header is always present.
 */
function KeyBadge({ usingKeylessFallback }: { usingKeylessFallback: boolean }) {
  if (!isConfigured.weather) {
    return (
      <span
        className="inline-flex shrink-0"
        title="Showing keyless data from api.open-meteo.com. Add VITE_OPENWEATHER_API_KEY to .env (then restart the dev server) for OpenWeatherMap."
      >
        <Pill tone="warn" testId="weather-unconfigured">
          add key
        </Pill>
      </span>
    );
  }
  if (usingKeylessFallback) {
    return (
      <span
        className="inline-flex shrink-0"
        title="OpenWeatherMap rejected the configured key, so this is keyless data from api.open-meteo.com."
      >
        <Pill tone="negative">key failed</Pill>
      </span>
    );
  }
  return null;
}

/** Staleness is shown as age, never as an error — a pinned tab goes offline. */
function freshness(lastUpdated: number | null): string {
  if (lastUpdated === null) return '';
  const at = new Date(lastUpdated);
  const stale = localDateKey(at) !== localDateKey();
  return stale ? 'updated yesterday' : `updated ${formatClockTime(at)}`;
}

function WeatherSkeleton() {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex items-center gap-3">
        <SkeletonBlock className="h-10 w-10 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <SkeletonLine w="w-24" h="h-7" />
          <SkeletonLine w="w-32" />
        </div>
      </div>
      <SkeletonBlock className="h-9 w-full" />
      <div className="mt-auto grid grid-cols-5 gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <SkeletonBlock key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );
}
